/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --background: var(--nve-sys-layer-canvas-background);
  --color: var(--nve-sys-layer-canvas-color);
  --gap: var(--nve-ref-space-md);
  --padding: 0;
  display: block;
  height: 100dvh;
  width: stretch;
  overflow: hidden;
}

:host([document-scroll]) {
  height: auto !important;
}

[internal-host] {
  background: var(--background);
  color: var(--color);
  height: 100%;
  display: grid;
  grid-template-rows: auto auto 1fr auto auto;
  grid-template-columns: auto auto 1fr auto auto;
  grid-template-areas:
    'header header header header header'
    'left-aside subheader subheader subheader right-aside'
    'left-aside left main right right-aside'
    'left-aside left bottom right right-aside'
    'left-aside subfooter subfooter subfooter right-aside'
    'footer footer footer footer footer';
}

slot {
  display: flex;
  background: var(--background);
  scroll-behavior: smooth;
  scrollbar-color: var(--nve-sys-scrollbar-thumb-color) var(--nve-sys-scrollbar-track-color);
  scrollbar-width: var(--nve-sys-scrollbar-width);
}

[name='header'] {
  grid-area: header;
  flex-direction: column;
}

[name='subheader'] {
  grid-area: subheader;
  flex-direction: column;
}

/* Keep overlapping panel borders above positioned main content. */
[name='left'],
[name='right'] {
  z-index: 1;
}

[name='left'] {
  grid-area: left;
  height: 100%;
}

[name='right'] {
  grid-area: right;
  height: 100%;
}

[name='bottom'] {
  grid-area: bottom;
  height: fit-content;
  flex-direction: column;
}

/** ensures sliding panels transition "under" aside slot */
[name='left-aside'],
[name='right-aside'] {
  position: relative;
  z-index: 2;
}

[name='left-aside'] {
  grid-area: left-aside;
  height: 100%;
}

[name='right-aside'] {
  grid-area: right-aside;
  height: 100%;
}

[name='subfooter'] {
  grid-area: subfooter;
}

[name='footer'] {
  grid-area: footer;
}

::slotted(nve-page-panel) {
  --border: 0;
}

::slotted(nve-toolbar[slot='footer']),
::slotted(nve-toolbar[slot='subfooter']),
::slotted(nve-page-panel[slot='footer']),
::slotted(nve-page-panel[slot='subfooter']) {
  border-block-start: var(--nve-ref-border-width-sm) solid var(--nve-ref-border-color-muted);
  width: 100%;
}

::slotted(nve-page-panel[slot='left']),
::slotted(nve-page-panel[slot='left-aside']),
::slotted(nve-toolbar[slot='left-aside']) {
  border-inline-end: var(--nve-ref-border-width-sm) solid var(--nve-ref-border-color-muted);
}

::slotted(nve-page-panel[slot='right']),
::slotted(nve-page-panel[slot='right-aside']),
::slotted(nve-toolbar[slot='right-aside']) {
  border-inline-start: var(--nve-ref-border-width-sm) solid var(--nve-ref-border-color-muted);
}

::slotted(nve-page-panel[slot='bottom']) {
  border-block-start: var(--nve-ref-border-width-sm) solid var(--nve-ref-border-color-muted);
}

::slotted(nve-page-panel[slot='subheader']),
::slotted(nve-toolbar[slot='subheader']) {
  border-block-end: var(--nve-ref-border-width-sm) solid var(--nve-ref-border-color-muted);
}

::slotted(nve-toolbar) {
  --border-radius: 0;
  --box-shadow: none;
}

::slotted(nve-toolbar[slot='subheader']) ::slotted(nve-toolbar[slot='subfooter']),
::slotted(nve-toolbar[slot='footer']) {
  height: fit-content;
}

::slotted(nve-toolbar[slot='left-aside']),
::slotted(nve-toolbar[slot='right-aside']) {
  --min-height: 100%;
}

::slotted(nve-resize-handle[slot]) {
  --background: transparent;
}

::slotted(nve-resize-handle[slot='left']) {
  margin-inline-start: calc(-1 * var(--nve-ref-border-width-md));
}

::slotted(nve-resize-handle[slot='right']) {
  margin-inline-end: calc(-1 * var(--nve-ref-border-width-md));
}

slot:not([name]) {
  gap: var(--gap);
  padding: var(--padding);
  display: flex;
  flex-direction: column;
  align-items: start;
  overflow: auto;

  slot {
    display: contents;
  }
}

::slotted(main) {
  width: 100%;
  height: 100%;
}
